import workerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

/** Longest side of the thumbnail, in pixels. Shown at ≤ 300px, so 2× for a sharp screen. */
export const THUMBNAIL_PX = 600;

/**
 * The first page of a design PDF as a PNG (SLD-0004).
 *
 * Drawn in the browser with pdf.js, which is loaded only when a thumbnail is
 * needed: it is the largest thing in the bundle by far, and the review screen
 * never wants it. The rest of the design is irrelevant to a thumbnail -- a
 * stitch design is one page, and the solver refuses anything else anyway.
 */
export async function renderThumbnail(pdf: Blob): Promise<Blob> {
  const pdfjs = await import('pdfjs-dist');
  pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

  const data = new Uint8Array(await pdf.arrayBuffer());
  const doc = await pdfjs.getDocument({ data }).promise;
  try {
    const page = await doc.getPage(1);
    const unscaled = page.getViewport({ scale: 1 });
    const scale = THUMBNAIL_PX / Math.max(unscaled.width, unscaled.height);
    const viewport = page.getViewport({ scale });

    const canvas = document.createElement('canvas');
    canvas.width = Math.ceil(viewport.width);
    canvas.height = Math.ceil(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) throw new Error('no 2d canvas');
    // A design with a transparent background would otherwise draw black.
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    await page.render({ canvasContext: context, viewport }).promise;

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob); else reject(new Error('could not encode the thumbnail'));
      }, 'image/png');
    });
  } finally {
    await doc.destroy();
  }
}
